import { ReactNode, useMemo, useState } from "react";
import { newUuid } from "@/utils/uuid";
import { get } from "lodash";
import styles from "./DropContainer.module.css";
import { classNames } from "@/utils/classNames";
import { SelectFilesButtonProps } from "./SelectFilesButton";
import { FileUploadConfig } from "@/fileUpload/fileUploadDomain";
import { useDispatch } from "@/stores/RootStoreContext";

export function DropContainer({
  className,
  children,
  disabled,
  onActivate,
  onDeactivate,
  onDrop,
  fileUploadConfig,
}: {
  className?: string;
  children: ReactNode | ((params: { active: boolean }) => ReactNode);
  disabled?: boolean;
  onActivate?: () => void;
  onDeactivate?: () => void;
  onDrop?: SelectFilesButtonProps["onChange"];
  fileUploadConfig: FileUploadConfig;
}) {
  const dispatch = useDispatch();
  const id = useMemo(() => newUuid(), []);
  const [active, setActive] = useState(false);

  return (
    <div
      id={id}
      className={classNames(className, active && styles.dragActive)}
      onDragOver={(event) => {
        event.preventDefault();
        if (!disabled) {
          setActive(true);
          onActivate?.();
        }
      }}
      onDragLeave={(event) => {
        event.preventDefault();
        if (active && get(event.target, "id") === id) {
          setActive(false);
          onDeactivate?.();
        }
      }}
      onDrop={(event) => {
        event.preventDefault();
        if (!active) {
          return;
        }
        const files = Array.from(event.dataTransfer.files);
        if (files.length) {
          setActive(false);
          const events = onDrop?.(files, fileUploadConfig);
          if (events) {
            dispatch(events);
          }
          onDeactivate?.();
        } else {
          setActive(false);
          onDeactivate?.();
        }
      }}
    >
      {typeof children === "function" ? children({ active }) : children}
    </div>
  );
}
